import React, { useEffect, useMemo, useState } from "react";
import { muAxios } from "../../api/axios";
import { useAuthContext } from "../../hooks/useAuthContext";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactPlayer from "react-player";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../utils/errorMessage";
import { PageSkeleton } from "../../components/Skeleton";

function Lesson() {
  const { user } = useAuthContext();
  const { notifyError, notifyPromise, notifySuccess } = useToast();
  const nav = useNavigate();

  const { course, chapter, lesson } = useParams();
  const parsedChapter = Number(chapter);
  const parsedLesson = Number(lesson);

  const [logo, setLogo] = useState("");
  const [title, setTitle] = useState("");

  const [lessonTitle, setLessonTitle] = useState("");
  const [vidFile, setVidFile] = useState("");

  const [vidOrder, setVidOrder] = useState([]);
  const [progress, setProgress] = useState(0);

  const [questionAnswer, setQuestionAnswer] = useState([]);
  const [userAnswers, setUserAnswers] = useState([]);

  const [instruction, setInstruction] = useState("");
  const [script, setScript] = useState("");
  const [userScript, setUserScript] = useState("");

  const [succMsg, setSuccMsg] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [pageNotFound, setPageNotFound] = useState(false);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [videoEnded, setVideoEnded] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(16 / 9);

  // clamp supaya tak jadi pelik (avoid extreme)
  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

    // fallback mapping by ratio (bagi stable UI)
    const mapRatio = (r) => {
    if (!r || !isFinite(r)) return 16 / 9;

    // Normalize: clamp dulu (avoid 0.1, 99)
    const x = clamp(r, 0.5, 3);

    // Portrait / phone-ish
    if (x <= 0.85) return 9 / 16;      // 0.5625

    // 1:1 square-ish
    if (x <= 1.15) return 1;           // 1.0

    // 4:3 / old cam
    if (x <= 1.45) return 4 / 3;       // 1.333

    // 16:9 / standard
    if (x <= 2.05) return 16 / 9;      // 1.777

    // Ultrawide
    return 21 / 9;                      // 2.333
    };

    // untuk elak layout jadi “terlampau tinggi” bila portrait
    const computedMaxHeight = (() => {
    // portrait => max 70vh
    if (aspectRatio <= 0.85) return "70vh";
    // square/4:3 => max 75vh
    if (aspectRatio <= 1.45) return "75vh";
    // standard/ultrawide => max 80vh
    return "80vh";
    })();

  const headers = useMemo(
    () => ({ headers: { accessToken: user?.token } }),
    [user?.token]
  );

  const vidUrl = useMemo(() => {
    if (!vidFile) return "";
    return `${process.env.REACT_APP_MU}/${vidFile}`;
  }, [vidFile]);

  const isExternalVideo = useMemo(() => (vidFile || "").startsWith("https://"), [vidFile]);

  const unlocked = useMemo(() => {
    const p = Number(progress || 0);
    if (!p) return false;
    return p >= parsedLesson;
  }, [progress, parsedLesson]);

  const unlockMessage = useMemo(() => {
    const p = Number(progress || 0);
    if (!p) return "You don’t have access to this lesson yet.";
    if (p < parsedLesson)
      return `You don’t have access to this lesson. Complete ${parsedLesson - p} more lesson(s) to unlock it.`;
    return "";
  }, [progress, parsedLesson]);

  const questionList = useMemo(() => (questionAnswer || []).map((q) => q.question), [questionAnswer]);

  const buttonContent = useMemo(() => {
    if ((questionAnswer || []).length === 0 && instruction === "") return "Proceed";
    return "Submit & Proceed";
  }, [questionAnswer, instruction]);

  // Load everything
  useEffect(() => {
    let mounted = true;

    const run = async () => {
      if (!user?.token || !course || !chapter || !lesson) return;

      setLoading(true);
      setErrMsg("");
      setSuccMsg("");
      setPageNotFound(false);

      try {
        const [pRes, courseRes, chapRes, dataRes, qRes, sRes] = await Promise.all([
          muAxios.get(`/api/mu/get/${course}/progress`, headers),
          muAxios.get(`/api/mu/get/course/${course}`, headers),
          muAxios.get(`/api/mu/get/chapter/${course}/${chapter}`, headers),
          muAxios.get(`/api/mu/get/data/${course}/${chapter}/${lesson}`, headers),
          muAxios.get(`/api/mu/get/question/${course}/${lesson}`, headers),
          muAxios.get(`/api/mu/get/script/${course}/${lesson}`, headers),
        ]);

        if (!mounted) return;

        setProgress(Number(pRes?.data?.progress || 0));

        const cjson = courseRes?.data;
        if (cjson) {
          setLogo(process.env.REACT_APP_MU + cjson.logo);
          setTitle(cjson.title || "");
        }

        const chjson = chapRes?.data;
        const chapterVidOrder = Array.isArray(chjson?.vidOrder) ? chjson.vidOrder.map(Number) : [];
        if (chjson) setVidOrder(chapterVidOrder);

        const djson = dataRes?.data;
        const lessonExistsInChapter = chapterVidOrder.includes(parsedLesson);
        const hasLessonPayload =
          !!djson &&
          typeof djson === "object" &&
          Object.keys(djson).length > 0;

        if (!lessonExistsInChapter || !hasLessonPayload) {
          setPageNotFound(true);
          setLessonTitle("");
          setVidFile("");
          setQuestionAnswer([]);
          setInstruction("");
          setScript("");
          setUserScript("");
          return;
        }

        if (djson) {
          setLessonTitle(djson.lessonTitle || "");
          setVidFile(djson.vidFile || "");
        }

        setQuestionAnswer(Array.isArray(qRes?.data) ? qRes.data : []);

        const sjson = sRes?.data || {};
        setInstruction(sjson.instruction || "");
        setScript(sjson.script || "");
        setUserScript(sjson.userScript || "");
      } catch (e) {
        notifyError(getErrorMessage(e, "Failed to load lesson. Please refresh and try again."));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    run();
    return () => (mounted = false);
  }, [user?.token, course, chapter, lesson, headers, notifyError]);

  const handleAnswerChange = (value, i) => {
    setUserAnswers((prev) => {
      const next = [...prev];
      next[i] = value;
      return next;
    });
  };

  const importScript = async (e) => {
    e.preventDefault();
    try {
      const res = await muAxios.get(`/api/mu/get/script/${course}/${lesson}`, headers);
      if (res?.data?.script != null) setUserScript(res.data.script);
      notifySuccess("Course script imported.");
    } catch (err) {
      notifyError(getErrorMessage(err, "Failed to import script."));
    }
  };

  const goNext = (json) => {
    if (json?.isLastVideo === "Yes" && json?.isLastSession === "No") {
      nav(`/courses/${course}/${parsedChapter + 1}`, { replace: true });
      return;
    }
    if (json?.isLastVideo === "Yes" && json?.isLastSession === "Yes") {
      nav(`/courses/${course}`, { replace: true });
      return;
    }
    nav(`/courses/${course}/${chapter}/${parsedLesson + 1}`, { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrMsg("");
    setSuccMsg("");

    if (!unlocked) {
      notifyError("This lesson is locked.");
      return;
    }

    // Validate required answers
    if ((questionAnswer || []).length > 0) {
      const need = questionAnswer.length;
      const filled = (userAnswers || []).filter((x) => x != null && x !== "").length;
      if (filled < need) {
        notifyError("Please answer all questions.");
        return;
      }
    }

    // Validate script if required
    if (instruction && (userScript || "").trim() === "") {
      notifyError("Please complete your script.");
      return;
    }

    const payload = { questions: questionList, answers: userAnswers, script: userScript };

    try {
      setSubmitting(true);
    
      notifyPromise(
        Promise.resolve(), // atau buang terus notifyPromise
        { loading: "Submitting..." },
        { position: "bottom-right", duration: 3000 }
      );
    
      const res = await muAxios.post(`/api/mu/submitLesson/${course}/${chapter}/${lesson}`, payload, headers);
      const json = res?.data || {};
    
      if (json?.qError) {
        notifyError(json.qError);
        setErrMsg(json.qError);
        return;
      }

      const successMessage =
        json?.success ||
        (json?.alreadyCompleted ? `Lesson ${lesson} was already completed.` : "");

      if (successMessage) {
        notifySuccess(successMessage);
        setSuccMsg(successMessage);
        setTimeout(() => goNext(json), json?.alreadyCompleted ? 300 : 1200);
        return;
      }

      const fallbackMessage = "Unexpected response after submitting lesson.";
      notifyError(fallbackMessage);
      setErrMsg(fallbackMessage);
    } catch (e) {
      const message =
        e?.response?.data?.message ||
        getErrorMessage(e, "Failed to submit lesson. Please try again.");
      notifyError(message);
      setErrMsg(message);
    } finally {
      setSubmitting(false);
    }    
  };

  if (loading) return <PageSkeleton variant="media" />;

  if (!loading && pageNotFound) {
    return (
      <div className="container py-6">
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item">
              <Link to="/courses">Courses</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to={`/courses/${course}`}>{title || course}</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to={`/courses/${course}/${chapter}`}>Chapter {chapter}</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Lesson {lesson}
            </li>
          </ol>
        </nav>

        <div className="card border-0 shadow-sm rounded-4">
          <div className="card-body py-5 text-center">
            <div className="fw-semibold mb-2" style={{ fontSize: 22 }}>
              Page not found
            </div>
            <div className="text-muted mx-auto" style={{ fontSize: 14, maxWidth: 520 }}>
              Lesson {lesson} does not exist in Chapter {chapter}.
            </div>

            <div className="d-flex justify-content-center gap-2 mt-4 flex-wrap">
              <Link className="btn btn-outline-secondary" to={`/courses/${course}/${chapter}`}>
                Back to Chapter
              </Link>
              <Link className="btn btn-dark" to={`/courses/${course}`}>
                Back to Course
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-6">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item">
            <Link to="/courses">Courses</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to={`/courses/${course}`}>{title || course}</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to={`/courses/${course}/${chapter}`}>Chapter {chapter}</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            Lesson {lesson}
          </li>
        </ol>
      </nav>

      {/* Header */}
      <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 mb-4">
        <div className="d-flex align-items-center gap-3">
          <div
            className="rounded-4 border bg-white d-flex align-items-center justify-content-center"
            style={{ width: 90, height: 70, padding: 8 }}
          >
            {logo ? (
              <img
                src={logo}
                alt={title || course}
                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
              />
            ) : (
              <span className="text-muted" style={{ fontSize: 12 }}>
                No logo
              </span>
            )}
          </div>

          <div>
            <div className="fw-semibold" style={{ fontSize: 18 }}>
              {title || "Course"}
            </div>
            <div className="text-muted" style={{ fontSize: 13 }}>
              Chapter {chapter} • Lesson {lesson}
            </div>
          </div>
        </div>

        <div className="text-muted" style={{ fontSize: 13 }}>
          {loading ? "Loading..." : `Current lesson: ${progress || 0}`}
        </div>
      </div>

      {/* Tabs */}
      <div className="card border-0 shadow-sm rounded-4 mb-3">
        <div className="card-body py-3">
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <Link className="btn btn-sm btn-outline-dark" to={`/courses/${course}/${chapter}`}>
              Overview
            </Link>

            {(vidOrder || []).map((value) => {
              const v = Number(value);
              const isLocked = Number(progress || 0) < v;
              const isActive = v === parsedLesson;

              return (
                <Link
                  key={v}
                  to={`/courses/${course}/${chapter}/${v}`}
                  className={`btn btn-sm ${
                    isActive ? "btn-dark" : isLocked ? "btn-outline-secondary disabled" : "btn-outline-dark"
                  }`}
                  aria-disabled={isLocked ? "true" : "false"}
                  tabIndex={isLocked ? -1 : 0}
                >
                  Lesson {v}
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Locked banner */}
      {!loading && !unlocked && (
        <div className="alert alert-warning d-flex align-items-center justify-content-between gap-3" role="alert">
          <div>
            <div className="fw-semibold">Lesson locked</div>
            <div style={{ fontSize: 13 }}>{unlockMessage}</div>
          </div>
          <Link className="btn btn-sm btn-outline-dark" to={`/courses/${course}/${chapter}`}>
            Back to Chapter
          </Link>
        </div>
      )}

      {/* === ONE COLUMN LAYOUT === */}
      <div className="row g-3">
        {/* Video full width */}
        <div className="col-12">
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body">
              <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
                <div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    LESSON {lesson}
                  </div>
                  <div className="fw-semibold" style={{ fontSize: 22 }}>
                    {loading ? "Loading..." : lessonTitle || "Lesson"}
                  </div>
                </div>

                {unlocked ? (
                  <span className="badge rounded-pill bg-success">Unlocked</span>
                ) : (
                  <span className="badge rounded-pill bg-light text-muted border">Preview</span>
                )}
              </div>

                <div className="rounded-4 border overflow-hidden bg-white">
                {/* Centered stage (no black container) */}
                <div
                    style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    background: "#fff",
                    padding: 12,
                    }}
                >
                    <div
                    style={{
                        width: "100%",
                        maxWidth: 1100, // adjust 1000-1300 ikut taste
                    }}
                    >
                    {/* ===== LOCAL VIDEO (mp4 stream) ===== */}
                    {!isExternalVideo ? (
                        <div
                        style={{
                            width: "100%",
                            position: "relative",
                            aspectRatio: String(aspectRatio || 16 / 9),
                            background: "#fff",
                            borderRadius: 16,
                            overflow: "hidden",
                        }}
                        >
                        {vidUrl ? (
                            <ReactPlayer
                            playsinline
                            controls
                            url={vidUrl}
                            muted
                            pip
                            width="100%"
                            height="100%"
                            className="react-player"
                            onEnded={() => setVideoEnded(true)}
                            onContextMenu={(e) => e.preventDefault()}
                            onReady={(player) => {
                                // Detect actual ratio for local <video>
                                try {
                                const internal = player?.getInternalPlayer?.();
                                const vw = internal?.videoWidth;
                                const vh = internal?.videoHeight;

                                if (vw && vh) setAspectRatio(vw / vh);
                                else setAspectRatio(16 / 9); // fallback
                                } catch (err) {
                                setAspectRatio(16 / 9);
                                }
                            }}
                            config={{
                                file: {
                                attributes: {
                                    controlsList: "nodownload",
                                },
                                },
                            }}
                            />
                        ) : (
                            <div className="p-4">
                            <div className="fw-semibold mb-1">No video found for this lesson.</div>
                            <div className="text-muted" style={{ fontSize: 13 }}>
                                Please contact admin to upload the video file.
                            </div>
                            </div>
                        )}

                        {/* Overlay if locked (LOCAL) */}
                        {!unlocked && (
                            <div
                            style={{
                                position: "absolute",
                                inset: 0,
                                background: "rgba(0,0,0,0.45)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: 16,
                                textAlign: "center",
                            }}
                            >
                            <div className="text-white">
                                <div className="fw-semibold mb-2" style={{ fontSize: 16 }}>
                                Lesson is locked
                                </div>
                                <div style={{ fontSize: 13, opacity: 0.9 }}>
                                Preview available. Complete earlier lessons to unlock.
                                </div>
                            </div>
                            </div>
                        )}
                        </div>
                    ) : (
                        /* ===== EXTERNAL VIDEO (https link) ===== */
                        <div
                        style={{
                            width: "100%",
                            position: "relative",
                            borderRadius: 16,
                            overflow: "hidden",
                            background: "#fff",
                            // external ikut viewport height, tapi still kemas
                            height: "60vh",
                            minHeight: 320,
                            maxHeight: 720,
                        }}
                        >
                        {vidUrl ? (
                            <ReactPlayer
                            playsinline
                            controls
                            url={vidUrl}
                            muted
                            pip
                            width="100%"
                            height="100%"
                            className="react-player"
                            onEnded={() => setVideoEnded(true)}
                            onContextMenu={(e) => e.preventDefault()}
                            />
                        ) : (
                            <div className="p-4">
                            <div className="fw-semibold mb-1">No video found for this lesson.</div>
                            <div className="text-muted" style={{ fontSize: 13 }}>
                                Please contact admin to update the video link.
                            </div>
                            </div>
                        )}

                        {/* Overlay if locked (EXTERNAL) */}
                        {!unlocked && (
                            <div
                            style={{
                                position: "absolute",
                                inset: 0,
                                background: "rgba(0,0,0,0.45)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: 16,
                                textAlign: "center",
                            }}
                            >
                            <div className="text-white">
                                <div className="fw-semibold mb-2" style={{ fontSize: 16 }}>
                                Lesson is locked
                                </div>
                                <div style={{ fontSize: 13, opacity: 0.9 }}>
                                Preview available. Complete earlier lessons to unlock.
                                </div>
                            </div>
                            </div>
                        )}
                        </div>
                    )}

                    {/* ===== Fallback / mapping (below player) ===== */}
                    <div className="d-flex align-items-center justify-content-between mt-2">
                        <div className="text-muted" style={{ fontSize: 12 }}>
                        Tip: Complete the questions/script (if any) to proceed.
                        </div>

                        {/* contoh status pill (optional) */}
                        <span className="badge rounded-pill text-bg-light border" style={{ fontWeight: 500 }}>
                        {videoEnded ? "Video finished" : "Video not finished"}
                        </span>
                    </div>
                    </div>
                </div>
                </div>
            </div>
          </div>
        </div>

        {/* Questions full width */}
        <div className="col-12">
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body">
              <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 mb-3">
                <div>
                  <div className="fw-semibold" style={{ fontSize: 18 }}>
                    Questions & Submission
                  </div>
                  <div className="text-muted" style={{ fontSize: 13 }}>
                    Answer the questions below to unlock the next lesson.
                  </div>
                </div>

                <div className="d-flex gap-2 flex-wrap">
                  <span className="badge rounded-pill bg-light text-muted border">
                    {(questionAnswer || []).length ? `${questionAnswer.length} question(s)` : "No questions"}
                  </span>
                  <span className="badge rounded-pill bg-light text-muted border">
                    {instruction ? "Script required" : "No script"}
                  </span>
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                {/* Questions */}
                {(questionAnswer || []).length > 0 && (
                  <div className="mt-2">
                    {questionAnswer.map((q, i) => (
                      <div
                        key={`${i}-${q.question}`}
                        className="mb-4 p-3 p-md-4 rounded-4 border"
                        style={{ background: "#fafafa" }}
                      >
                        <div className="d-flex align-items-center justify-content-between gap-3 mb-2">
                          <div className="fw-semibold" style={{ fontSize: 14 }}>
                            Question {i + 1}
                          </div>
                          <span className="badge rounded-pill bg-light text-muted border">
                            {userAnswers?.[i] ? "Answered" : "Not answered"}
                          </span>
                        </div>

                        <div className="text-muted mb-3" style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>
                          {q.question}
                        </div>

                        <select
                          className="form-select"
                          value={userAnswers?.[i] || ""}
                          onChange={(e) => handleAnswerChange(e.target.value, i)}
                          disabled={!unlocked || submitting}
                          required
                        >
                          <option value="" disabled>
                            Select your answer
                          </option>
                          {(q.answers || []).map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                )}

                {/* Script */}
                {instruction && (
                  <div className="mt-4">
                    <div className="fw-semibold mb-2" style={{ fontSize: 16 }}>
                      Build your own script
                    </div>
                    <div className="text-muted mb-3" style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>
                      {instruction}
                    </div>

                    {script && (
                      <button
                        className="btn btn-sm btn-outline-info mb-3"
                        onClick={importScript}
                        disabled={!unlocked || submitting}
                        type="button"
                      >
                        Import Course Script
                      </button>
                    )}

                    <textarea
                      className="form-control"
                      rows={8}
                      value={userScript}
                      onChange={(e) => setUserScript(e.target.value)}
                      disabled={!unlocked || submitting}
                      required
                    />
                  </div>
                )}

                {/* Alerts */}
                {succMsg && (
                  <div className="alert alert-success mt-3 mb-0" role="alert">
                    {succMsg}
                  </div>
                )}
                {errMsg && (
                  <div className="alert alert-danger mt-3 mb-0" role="alert">
                    {errMsg}
                  </div>
                )}

                {/* CTA */}
                <div className="d-grid d-md-flex gap-2 mt-4">
                <button
                    className="btn btn-success"
                    type="submit"
                    style={{
                        padding: "10px 18px",
                        fontSize: 14,
                        borderRadius: 10,
                        minWidth: 180,
                    }}
                    >
                    {submitting ? "Submitting..." : buttonContent}
                  </button>

                  <Link
                    className="btn btn-outline-secondary"
                    to={`/courses/${course}/${chapter}`}
                    style={{
                        padding: "10px 18px",
                        fontSize: 14,
                        borderRadius: 10,
                    }}
                    >
                    Back to Chapter
                  </Link>
                </div>

                {!unlocked && (
                  <div className="text-muted mt-3" style={{ fontSize: 12 }}>
                    You can preview this page, but submission is disabled until the lesson is unlocked.
                  </div>
                )}
              </form>
            </div>
          </div>

          <div className="text-muted mt-3" style={{ fontSize: 12 }}>
            Tip: If a lesson is locked, complete your current lesson to unlock the next one.
          </div>
        </div>
      </div>
    </div>
  );
}

export default Lesson;
